import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from '@packages/utils';
import {
  useEditSiteCoordinatorMutation,
  useEditWorkshopCoordinatorMutation,
} from 'redux/apiSlice/workshops.slice';
import {
  useEditSiteCoordinatorMutation as useEditEventSiteCoordinatorMutation,
  useEditEventCoordinatorMutation,
} from 'redux/apiSlice/events.slice';
import {
  useShowToastNotification,
  ENotificationType,
} from 'components/Base/Notification';
import { TOption, TRtkErrorType } from 'data/types/generic.types';
import { Select } from 'antd/lib';
import { TWorkshopDetailsDataUI } from 'data/types/workshops.types';
import Spinner from 'components/Base/Spinner';
import { Flex } from 'antd';
import { TCoordinatorType } from '../Coordinator.types';
import { TCreateCoordinatorModalOpen } from '../CreateCoordinatorModal/CreateCoordinatorModal.types';
import CoordinatorTag from './CoordinatorTag';
import CoordinatorDropdown from './CoordinatorDropdown';

type CoordinatorSelectorProps = {
  id: string;
  coordinators?: TWorkshopDetailsDataUI['siteCoordinators'];
  options?: TOption[];
  selectedCoordinators: any[];
  setSelectedCoordinators: React.Dispatch<React.SetStateAction<any[]>>;
  setOpen: React.Dispatch<React.SetStateAction<TCreateCoordinatorModalOpen>>;
  isLoading?: boolean;
  onBlur?: React.Dispatch<React.SetStateAction<boolean>>;
  coordinatorType: TCoordinatorType;
};

const CoordinatorTagRender = (props: any) => {
  return <CoordinatorTag {...props} />;
};
const CoordinatorDropdownRender = (props: any) => (
  <CoordinatorDropdown {...props} />
);

const CoordinatorSelector: FC<CoordinatorSelectorProps> = ({
  id,
  coordinators,
  options,
  selectedCoordinators,
  setSelectedCoordinators,
  setOpen,
  isLoading,
  onBlur,
  coordinatorType,
}) => {
  const isSiteCoordinator =
    coordinatorType === 'workshopSite' || coordinatorType === 'eventSite';
  const { t } = useTranslation({
    keyPrefix:
      'descriptions.admin-portal.workshops.details.basic-info.coordinator',
  });
  const { showToastNotification } = useShowToastNotification();

  const [initialName, setInitialName] = useState<string>('');

  useEffect(() => {
    if (!options?.length) return;

    const seen = new Set<string>();
    const selected =
      coordinators?.reduce<TOption[]>((acc, coordinator) => {
        if (!coordinator?.id || seen.has(coordinator.id)) return acc;
        const match = options.find(person => person.value === coordinator.id);
        if (!match) return acc;
        seen.add(coordinator.id);
        acc.push(match);
        return acc;
      }, []) ?? [];

    setSelectedCoordinators(selected);
  }, [coordinators, options]);

  const [editWorkshopSiteCoordinator] = useEditSiteCoordinatorMutation();
  const [editEventSiteCoordinator] = useEditEventSiteCoordinatorMutation();
  const [editWorkshopEventCoordinator] = useEditWorkshopCoordinatorMutation();
  const [editEventEventCoordinator] = useEditEventCoordinatorMutation();

  const editCoordinator = (() => {
    switch (coordinatorType) {
      case 'workshopSite':
        return editWorkshopSiteCoordinator;
      case 'eventSite':
        return editEventSiteCoordinator;
      case 'workshopEvent':
        return editWorkshopEventCoordinator;
      case 'eventEvent':
      default:
        return editEventEventCoordinator;
    }
  })();

  const onSearch = (value: string) => {
    setInitialName(value);
  };

  const onEdit = async (values: string[]) => {
    if (!options?.length) return;
    const uniqueValues = Array.from(new Set(values.filter(Boolean)));

    if (uniqueValues.length > 3) {
      showToastNotification({
        message: 'You can only select up to 3 coordinators',
        type: ENotificationType.INFO,
      });

      return;
    }

    if (isSiteCoordinator && !uniqueValues.length) {
      showToastNotification({
        message:
          'To be published, one Coordinator needs to be assigned to a workshop.',
        type: ENotificationType.INFO,
      });

      return;
    }

    const updatedSelectedCoordinators = uniqueValues
      .map(value => options?.find(person => person.value === value))
      .filter((option): option is TOption => Boolean(option));

    setSelectedCoordinators(updatedSelectedCoordinators);

    const params = {
      id,
      value: uniqueValues,
    };

    try {
      await editCoordinator(params).unwrap();
      showToastNotification({
        message: t('SUCCESS_MESSAGE'),
        type: ENotificationType.SUCCESS,
      });
    } catch (e) {
      const error = e as TRtkErrorType;
      const message =
        error?.status === 403 ? t('PERMISSION_ERROR') : error?.data?.message;
      showToastNotification({
        message,
        type: ENotificationType.ERROR,
      });
    }
  };

  if (isLoading)
    return (
      <Flex
        align="center"
        justify="center"
        style={{ minHeight: 32, minWidth: 140 }}
      >
        <Spinner />
      </Flex>
    );

  return (
    <Select
      mode="multiple"
      autoFocus
      showSearch
      onBlur={() => onBlur && onBlur(false)}
      onClick={e => {
        // this is a workaround so that the close event when you click on the add button will not clear the input field.
        if (
          e.currentTarget.tagName === 'DIV' &&
          e.currentTarget.className.includes('ant-select')
        ) {
          e.stopPropagation();
        }
      }}
      onChange={onEdit}
      onSearch={onSearch}
      value={selectedCoordinators?.map(coordinator => coordinator?.value)}
      options={options}
      filterOption={(input, option) =>
        option
          ? option.label.toLowerCase().includes(input.toLowerCase())
          : false
      }
      placeholder={t('EMPTY_TEXT')}
      tagRender={props =>
        CoordinatorTagRender({
          ...props,
          closable: selectedCoordinators?.length > 1,
          coordinators,
          setOpen,
        })
      }
      dropdownRender={menu =>
        CoordinatorDropdownRender({
          menu,
          initialName,
          setOpen,
          disabled: selectedCoordinators?.length === 3,
        })
      }
      style={{ minWidth: '50%', zIndex: 100 }}
    />
  );
};

export default CoordinatorSelector;
